/**
 * Helper chiusure calendario — usati una volta in formatCalendarData.
 * Nessun ricalcolo su scroll/touch.
 */

import dayjs from 'dayjs';

/** Normalizza "09:00" / "9:00:00" → "09:00:00". */
export const normalizeClosureTime = (value) => {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parts = raw.split(':');
  if (parts.length < 2) return null;
  const hours = String(Number(parts[0])).padStart(2, '0');
  const minutes = String(Number(parts[1] || 0)).padStart(2, '0');
  const seconds = String(Number(parts[2] || 0)).padStart(2, '0');
  if (!Number.isFinite(Number(parts[0])) || !Number.isFinite(Number(parts[1] || 0))) return null;
  return `${hours}:${minutes}:${seconds}`;
};

/** Giornata intera: dalle/alle entrambi 00:00:00 (non scartare come intervallo vuoto). */
export const isFullDayClosureSlot = (dalle, alle) => {
  const start = normalizeClosureTime(dalle);
  const end = normalizeClosureTime(alle);
  return start === '00:00:00' && end === '00:00:00';
};

const dateKey = (raw) => {
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const closureDateRaw = (item) =>
  item?.data ?? item?.data_chiusura ?? item?.dataChiusura ?? item?.giorno;

const toClosureSlot = (item) => ({
  dalle: normalizeClosureTime(item?.dalle) || '00:00:00',
  alle: normalizeClosureTime(item?.alle) || '00:00:00',
});

const filterClosureArrayForDate = (list, date) =>
  (Array.isArray(list) ? list : [])
    .filter((item) => dateKey(closureDateRaw(item)) === date)
    .map(toClosureSlot);

const resolveClosureMap = (chiusure, date) => {
  if (!chiusure || typeof chiusure !== 'object' || Array.isArray(chiusure)) return [];
  const dmy = dayjs(date).format('DD/MM/YYYY');
  const dmyShort = dayjs(date).format('DD/MM/YY');
  const slots = chiusure[date] || chiusure[dmy] || chiusure[dmyShort] || [];
  return (Array.isArray(slots) ? slots : []).map(toClosureSlot);
};

export const getWorkerClosuresForDate = (worker, date) => {
  const chiusure = worker?.chiusure;

  if (Array.isArray(chiusure)) {
    const fromChiusure = filterClosureArrayForDate(chiusure, date);
    if (fromChiusure.length) return fromChiusure;
  } else if (chiusure && typeof chiusure === 'object') {
    const fromMap = resolveClosureMap(chiusure, date);
    if (fromMap.length) return fromMap;
  }

  // GET_WORKERS / alcuni payload calendario usano `closures` (data_chiusura).
  return filterClosureArrayForDate(worker?.closures, date);
};

/** Intervallo chiusura in secondi unix, clipato alla fascia lavorativa del giorno. */
export const parseClosureInterval = (limit, date, startWork, endWork) => {
  const dalle = normalizeClosureTime(limit?.dalle);
  const alle = normalizeClosureTime(limit?.alle);
  if (!dalle || !alle) return null;

  if (isFullDayClosureSlot(dalle, alle)) {
    return startWork < endWork ? { start: startWork, end: endWork } : null;
  }

  let unixStart = dayjs(`${date}T${dalle}`).unix();
  let unixEnd = dayjs(`${date}T${alle === '00:00:00' ? '24:00:00' : alle}`).unix();

  if (dalle === '00:00:00') {
    unixStart = startWork;
  }
  if (alle === '00:00:00') {
    unixEnd = endWork;
  }

  const realStart = Math.max(unixStart, startWork);
  const realEnd = Math.min(unixEnd, endWork);
  if (realEnd <= realStart) return null;
  return { start: realStart, end: realEnd };
};

export const clipToWindow = (start, end, windowStart, windowEnd) => {
  const clippedStart = Math.max(start, windowStart);
  const clippedEnd = Math.min(end, windowEnd);
  if (clippedStart >= clippedEnd) return null;
  return { start: clippedStart, end: clippedEnd };
};

export const mergeIntervals = (intervals) => {
  if (!intervals.length) return [];

  const sorted = [...intervals].sort((a, b) => a.start - b.start);

  return sorted.reduce((acc, interval) => {
    const last = acc[acc.length - 1];
    if (last && interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
      return acc;
    }
    return [...acc, { ...interval }];
  }, []);
};

export const getIntervalsDuration = (intervals) =>
  mergeIntervals(intervals).reduce((acc, interval) => acc + (interval.end - interval.start), 0);

export const getAvailableIntervals = (startWork, endWork, closures = []) => {
  const closedIntervals = mergeIntervals(
    closures
      .map((closure) =>
        clipToWindow(closure.unixStartTime, closure.unixEndTime, startWork, endWork),
      )
      .filter(Boolean),
  );

  if (!closedIntervals.length) {
    return [{ start: startWork, end: endWork }];
  }

  const available = [];
  let cursor = startWork;

  closedIntervals.forEach((closed) => {
    if (cursor < closed.start) {
      available.push({ start: cursor, end: closed.start });
    }
    cursor = Math.max(cursor, closed.end);
  });

  if (cursor < endWork) {
    available.push({ start: cursor, end: endWork });
  }

  return available;
};

export const getOccupiedDurationInAvailableSlots = (tasks, availableIntervals) => {
  const occupied = [];

  tasks.forEach((task) => {
    availableIntervals.forEach((slot) => {
      const clipped = clipToWindow(task.unixStartTime, task.unixEndTime, slot.start, slot.end);
      if (clipped) occupied.push(clipped);
    });
  });

  return getIntervalsDuration(occupied);
};

/** Poltrona senza intervalli prenotabili nel giorno (availableTime === 0). */
export const isWorkerFullyClosed = (worker, startWork, endWork) => {
  const closures = (worker?.workerTasks || []).filter((task) => task.limitCard === true);
  if (!closures.length) return false;
  const available = getAvailableIntervals(startWork, endWork, closures);
  return getIntervalsDuration(available) === 0;
};

/** Giorno in cui tutte le poltrone sono fully closed. */
export const isDayFullyClosed = (workers = [], startWork, endWork) => {
  if (!workers.length) return false;
  return workers.every((worker) =>
    typeof worker.fullyClosed === 'boolean'
      ? worker.fullyClosed
      : isWorkerFullyClosed(worker, startWork, endWork),
  );
};

export const sameWorkerId = (a, b) => {
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

export const workerIdInList = (ids, workerId) =>
  (ids || []).some((id) => sameWorkerId(id, workerId));

export const HIDE_CLOSED_CHAIRS_PARAM = 'hideClosedChairs';
export const COMPACT_CLOSED_DAYS_PARAM = 'compactClosedDays';
/** Nasconde badge/icone sulle card prenotazione (filtri calendario). */
export const HIDE_TASK_ICONS_PARAM = 'hideTaskIcons';
/** Nasconde il badge orario (range) sulle card prenotazione. */
export const HIDE_TASK_TIME_PARAM = 'hideTaskTime';

export const isClosureFilterOn = (searchParams, key) => searchParams?.get?.(key) === '1';

/** Flags for POST getCalendario — omit keys when OFF (API default).
 * I toggle UI (compatta poltrone / giorni) restano client-side:
 * i flag API che rimuovono chiusure dal payload spezzano fullyClosed
 * e fanno “chiudi → riapri” dopo il refetch.
 * Reintrodurre solo se il back lascia stub con fullyClosed/limitCard.
 */
export const getClosureApiFlags = (_searchParams) => ({});
